import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import FloatingLabelInput from "../utils/FloatingLabelInput";
import "../../styles/components/LoginUniversal.css";

const OTP_RESEND_DEBOUNCE_INTERVAL_SECONDS = 60;

interface Props {
  email: string;
  error: string | null;
  verificationCode: string;
  onCodeSubmit: (e: React.FormEvent) => void;
  onCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCodeResend: () => void;
  onBackToLogin: () => void;
}

const OTPCodeVerificationView: React.FC<Props> = ({
  email,
  error,
  verificationCode,
  onCodeSubmit,
  onCodeChange,
  onCodeResend,
  onBackToLogin,
}) => {
  const { t } = useTranslation();
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  useEffect(() => {
    if (secondsRemaining > 0) {
      const timer = setTimeout(() => {
        setSecondsRemaining(secondsRemaining - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [secondsRemaining]);

  const handleResendClick = () => {
    if (secondsRemaining > 0) {
      return;
    }
    onCodeResend();
    setSecondsRemaining(OTP_RESEND_DEBOUNCE_INTERVAL_SECONDS);
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const isResendDisabled = secondsRemaining > 0;
  const resendButtonText = isResendDisabled 
    ? t("otp.sendAgainIn", { time: formatTime(secondsRemaining) })
    : t("otp.sendAgain");

  return (
    <div className="login-options">
      <div className="verification-info">
        {t("otp.enterCodeSent")}
        <br />
        {email}
      </div>

      <form onSubmit={onCodeSubmit} className="email-login">
        <FloatingLabelInput
          label={t("otp.sixDigitCode")}
          type="number"
          pattern="[0-9]*"
          name="code"
          value={verificationCode}
          onChange={onCodeChange}
          maxLength={6}
          style={{ letterSpacing: "2px" }}
          required
        />
        {error && <div className="otp-error-message">{error}</div>}
        <div className="otp-resend-container">
          <span className="otp-resend-label">{t("otp.didntGetCode")}</span>
          <button
            type="button"
            className={`otp-resend-button${isResendDisabled ? ' dimmed' : ''}`}
            onClick={handleResendClick}
            disabled={isResendDisabled}
          >
            {resendButtonText}
          </button>
        </div>
        <button
          type="submit"
          className="entryflow-submit-button"
          disabled={verificationCode.length !== 6}
        >
          <span>{t("otp.verify")}</span>
        </button>
      </form>
      <button type="button" onClick={onBackToLogin} className="back-to-login-button">
        {t("otp.backToSignIn")}
      </button>
    </div>
  );
};

export default OTPCodeVerificationView;
